import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';
import api from '../lib/api';

/**
 * Shown after a user logs in with a temporary password set by the admin.
 * User cannot access the app until they pick a new password.
 */
const ChangePasswordRequiredPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Basic strength metrics
  const hasMin = newPassword.length >= 8;
  const hasLower = /[a-z]/.test(newPassword);
  const hasUpper = /[A-Z]/.test(newPassword);
  const hasDigit = /\d/.test(newPassword);
  const hasSymbol = /[^A-Za-z0-9]/.test(newPassword);
  const matches = newPassword && newPassword === confirmPassword;
  const strong = hasMin && ((hasLower && hasUpper && hasDigit) || (hasLower && hasUpper && hasSymbol));
  const canSubmit = currentPassword && hasMin && matches && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/change-password-first-time', {
        currentPassword,
        newPassword,
      });
      // Update stored user object to clear the flag
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const u = JSON.parse(stored);
          u.mustChangePassword = false;
          localStorage.setItem('user', JSON.stringify(u));
        }
      } catch { /* noop */ }
      setSuccess(true);
      setTimeout(() => navigate('/'), 1400);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה בשינוי הסיסמה');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#1565C0] via-[#311B92] to-[#6200EA]"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="inline-flex bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-3 mb-3">
            <Lock size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-black text-gray-900">החלפת סיסמה נדרשת</h1>
          <p className="text-sm text-gray-500 mt-1">
            החשבון שלך נפתח על ידי צוות Amutech. כדי להמשיך — בחר/י סיסמה חדשה וחזקה.
          </p>
        </div>

        {success ? (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
            <div className="inline-flex bg-green-500 rounded-full p-2 mb-2">
              <Check size={20} className="text-white" />
            </div>
            <p className="text-green-800 font-bold">הסיסמה עודכנה!</p>
            <p className="text-green-700 text-xs mt-1">מעביר למערכת...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                סיסמה נוכחית (זו שקיבלת במייל)
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoFocus
                  dir="ltr"
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute top-1/2 -translate-y-1/2 left-2 text-gray-400"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                סיסמה חדשה (לפחות 8 תווים)
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                dir="ltr"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />

              {newPassword && (
                <div className="mt-2 space-y-1 text-xs">
                  <div className={`flex items-center gap-1 ${hasMin ? 'text-green-600' : 'text-gray-400'}`}>
                    {hasMin ? '✓' : '○'} לפחות 8 תווים
                  </div>
                  <div className={`flex items-center gap-1 ${hasLower && hasUpper ? 'text-green-600' : 'text-gray-400'}`}>
                    {hasLower && hasUpper ? '✓' : '○'} אות גדולה ואות קטנה
                  </div>
                  <div className={`flex items-center gap-1 ${hasDigit || hasSymbol ? 'text-green-600' : 'text-gray-400'}`}>
                    {hasDigit || hasSymbol ? '✓' : '○'} ספרה או סימן מיוחד
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                אימות סיסמה חדשה
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                dir="ltr"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 ${
                  confirmPassword && !matches
                    ? 'border-red-400 focus:ring-red-400'
                    : 'border-gray-300 focus:ring-indigo-400'
                }`}
              />
              {confirmPassword && !matches && (
                <p className="mt-1 text-xs text-red-500">הסיסמאות לא תואמות</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                ⚠️ {error}
              </div>
            )}

            {!strong && newPassword && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs">
                💡 עדיין אפשר להמשיך, אבל מומלץ לחזק את הסיסמה (אות גדולה + קטנה + ספרה)
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-black disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-700 hover:to-purple-700 transition-colors"
            >
              {loading ? 'מעדכן...' : 'עדכן סיסמה והמשך'}
            </button>

            <button
              type="button"
              onClick={logout}
              className="w-full text-xs text-gray-500 hover:text-gray-700"
            >
              יציאה מהחשבון
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordRequiredPage;
